import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { Node } from '@babylonjs/core/node';
import type { SceneObject } from '../core/SceneObject';
import type { LayoutSizeProvider } from './layout';
import { COLLIDER_MESH_SUFFIX, SELECTION_MESH_SUFFIX } from './boxcollider';

/** Internal provider for opted-in free geometry; the ordinary provider path owns fitting. */
export class GeometrySizeProvider implements LayoutSizeProvider {
    readonly layoutContentFrame = 'parent' as const;
    readonly layoutFit = 'none' as const;

    constructor(private readonly object: SceneObject) {}

    getLayoutContentSize() { return this.measure('parent')?.size ?? null; }
    getLayoutContentCenter() { return this.measure('parent')?.center ?? null; }
    getLayoutIntrinsicSize() { return this.measure('intrinsic')?.size ?? null; }

    /** A solve can resize descendants after measurement; only those changes need another pass. */
    watchBounds(options: { intrinsic: boolean; epsilon: number }): () => boolean {
        const basis = options.intrinsic ? 'intrinsic' : 'parent';
        const before = this.measure(basis);
        return () => {
            const after = this.measure(basis);
            if (!before || !after) return before !== after;
            return (['size', 'center'] as const).some(key =>
                (['x', 'y', 'z'] as const).some(axis =>
                    Math.abs(before[key][axis] - after[key][axis]) > options.epsilon));
        };
    }

    private includes(mesh: AbstractMesh): boolean {
        if (!mesh.isVisible || mesh.visibility <= 0 || !mesh.isEnabled()
            || mesh.infiniteDistance || mesh.getTotalVertices() === 0) return false;
        const name = mesh.name;
        if ((name.startsWith('__') && !name.startsWith('__builtin_'))
            || name.endsWith(COLLIDER_MESH_SUFFIX) || name.endsWith(SELECTION_MESH_SUFFIX)) return false;
        for (let node: Node | null = mesh; node; node = node.parent) {
            const flags = node.metadata as Record<string, unknown> | null | undefined;
            if (node.doNotSerialize || flags?.editorOnly || flags?.editorHandle
                || flags?.doNotSerialize || flags?.hidden) return false;
        }
        return true;
    }

    private measure(basis: 'parent' | 'intrinsic') {
        const root = this.object.transform;
        const frame = basis === 'intrinsic' ? root : root.parent;
        const world = frame?.computeWorldMatrix(true) ?? Matrix.Identity();
        if (world.determinant() === 0) return null;
        let toFrame = Matrix.Invert(world);
        // A slot replaces the root scale, but keeps its rotation. Measure that
        // scale-one basis directly, never divide by the previous fitted size.
        if (basis === 'intrinsic') {
            const rotation = root.rotationQuaternion ?? Quaternion.FromEulerVector(root.rotation);
            toFrame = toFrame.multiply(Matrix.Compose(Vector3.One(), rotation, Vector3.Zero()));
        }
        const min = new Vector3(Infinity, Infinity, Infinity);
        const max = new Vector3(-Infinity, -Infinity, -Infinity);
        const point = new Vector3();
        for (const mesh of root.getChildMeshes(false)) {
            if (!this.includes(mesh)) continue;
            mesh.computeWorldMatrix(true);
            for (const corner of mesh.getBoundingInfo().boundingBox.vectorsWorld) {
                Vector3.TransformCoordinatesToRef(corner, toFrame, point);
                min.minimizeInPlace(point);
                max.maximizeInPlace(point);
            }
        }
        const size = { x: max.x - min.x, y: max.y - min.y, z: max.z - min.z };
        if (!Object.values(size).every(value => Number.isFinite(value) && value >= 0)) return null;
        const origin = basis === 'parent' ? root.position : Vector3.Zero();
        return {
            size,
            center: {
                x: (min.x + max.x) / 2 - origin.x,
                y: (min.y + max.y) / 2 - origin.y,
                z: (min.z + max.z) / 2 - origin.z,
            },
        };
    }
}
